/**
 * Страница "О системе" — описание методологии.
 * Формулы расчёта ИПС, весовые коэффициенты, алгоритм трендов,
 * архитектура системы. Полезна для защиты диплома.
 */

import React from 'react';

const AboutPage: React.FC = () => {
  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 24 }}>О системе MS Health Monitor</h1>

      <Section title="Назначение">
        <p>
          Система мониторинга состояния здоровья пациентов с рассеянным склерозом (РС).
          Обеспечивает сбор данных о симптомах, приёме лекарств и когнитивно-моторном статусе,
          автоматический расчёт интегрального показателя состояния (ИПС),
          выявление факторов риска и поддержку принятия клинических решений.
        </p>
      </Section>

      <Section title="Архитектура">
        <table style={styles.table}>
          <tbody>
            <tr><td style={styles.td}><b>Frontend</b></td><td style={styles.td}>React 18, TypeScript, Recharts</td></tr>
            <tr><td style={styles.td}><b>Backend</b></td><td style={styles.td}>Python 3, Flask, SQLAlchemy ORM</td></tr>
            <tr><td style={styles.td}><b>База данных</b></td><td style={styles.td}>SQLite (dev) / PostgreSQL (prod)</td></tr>
            <tr><td style={styles.td}><b>Аутентификация</b></td><td style={styles.td}>JWT (access + refresh tokens)</td></tr>
            <tr><td style={styles.td}><b>API</b></td><td style={styles.td}>REST, JSON</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Интегральный показатель состояния (ИПС)">
        <p>ИПС отражает общую тяжесть состояния пациента по шкале от 0 (норма) до 10 (критическое).</p>
        <div style={styles.formula}>
          ИПС = 0.6 &times; S<sub>symptoms</sub> + 0.4 &times; S<sub>tests</sub>
        </div>
        <p>где:</p>
        <ul>
          <li><b>S<sub>symptoms</sub></b> — взвешенная средняя тяжесть симптомов за 7 дней</li>
          <li><b>S<sub>tests</sub></b> — средний нормализованный балл тестов (когнитивный + моторный)</li>
        </ul>
        <p>Если результаты тестов отсутствуют, используется только симптоматическая оценка (вес 1.0).</p>
      </Section>

      <Section title="Взвешенная оценка симптомов">
        <div style={styles.formula}>
          S<sub>symptoms</sub> = &Sigma;(w<sub>i</sub> &times; severity<sub>i</sub>) / &Sigma;w<sub>i</sub>
        </div>
        <p>Веса симптомов (по шкале EDSS):</p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Симптом</th>
              <th style={styles.th}>Вес (w)</th>
              <th style={styles.th}>Категория</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={styles.td}>Усталость</td><td style={styles.td}>0.20</td><td style={styles.td}>Физический</td></tr>
            <tr><td style={styles.td}>Нарушения зрения</td><td style={styles.td}>0.15</td><td style={styles.td}>Сенсорный</td></tr>
            <tr><td style={styles.td}>Онемение</td><td style={styles.td}>0.15</td><td style={styles.td}>Сенсорный</td></tr>
            <tr><td style={styles.td}>Проблемы с балансом</td><td style={styles.td}>0.15</td><td style={styles.td}>Физический</td></tr>
            <tr><td style={styles.td}>Мышечная слабость</td><td style={styles.td}>0.15</td><td style={styles.td}>Физический</td></tr>
            <tr><td style={styles.td}>Когнитивные проблемы</td><td style={styles.td}>0.10</td><td style={styles.td}>Когнитивный</td></tr>
            <tr><td style={styles.td}>Боль</td><td style={styles.td}>0.10</td><td style={styles.td}>Физический</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Нормализация тестовых баллов">
        <p>Все сырые баллы тестов нормализуются в шкалу 0-10 (0 = лучший, 10 = худший):</p>
        <div style={styles.formula}>
          <b>lower_is_better:</b> norm = (score / max_score) &times; 10
        </div>
        <div style={styles.formula}>
          <b>higher_is_better:</b> norm = (1 - score / max_score) &times; 10
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Тест</th>
              <th style={styles.th}>Тип</th>
              <th style={styles.th}>Max score</th>
              <th style={styles.th}>Направление</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={styles.td}>Время реакции</td><td style={styles.td}>Когнитивный</td><td style={styles.td}>1000 мс</td><td style={styles.td}>lower_is_better</td></tr>
            <tr><td style={styles.td}>Последовательности</td><td style={styles.td}>Когнитивный</td><td style={styles.td}>100</td><td style={styles.td}>higher_is_better</td></tr>
            <tr><td style={styles.td}>Точность нажатий</td><td style={styles.td}>Моторный</td><td style={styles.td}>100%</td><td style={styles.td}>higher_is_better</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Анализ трендов">
        <p>Тренд определяется по дельте скользящих средних ИПС (текущая неделя минус предыдущая):</p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Дельта (&Delta;)</th>
              <th style={styles.th}>Тренд</th>
              <th style={styles.th}>Описание</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={styles.td}>&Delta; &le; -1.0</td><td style={{ ...styles.td, color: '#16a34a', fontWeight: 600 }}>Улучшение</td><td style={styles.td}>Состояние улучшается</td></tr>
            <tr><td style={styles.td}>-1.0 &lt; &Delta; &lt; 1.0</td><td style={{ ...styles.td, color: '#2563eb', fontWeight: 600 }}>Стабильно</td><td style={styles.td}>Без значимых изменений</td></tr>
            <tr><td style={styles.td}>1.0 &le; &Delta; &lt; 2.0</td><td style={{ ...styles.td, color: '#f59e0b', fontWeight: 600 }}>Ухудшение</td><td style={styles.td}>Заметное ухудшение</td></tr>
            <tr><td style={styles.td}>&Delta; &ge; 2.0</td><td style={{ ...styles.td, color: '#dc2626', fontWeight: 600 }}>Критично</td><td style={styles.td}>Требует немедленного внимания</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Флаги риска">
        <p>Автоматически генерируемые предупреждения для врача. Пороги настраиваемые для каждого пациента.</p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Флаг</th>
              <th style={styles.th}>Условие (по умолчанию)</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={styles.td}>high_score</td><td style={styles.td}>ИПС &ge; 7.0</td></tr>
            <tr><td style={styles.td}>worsening_trend</td><td style={styles.td}>&Delta; &ge; 2.0</td></tr>
            <tr><td style={styles.td}>extreme_symptom</td><td style={styles.td}>Любой симптом &ge; 9 в течение 3+ дней подряд</td></tr>
            <tr><td style={styles.td}>missed_medication</td><td style={styles.td}>Пропуск приёма лекарств &gt; 2 дней подряд</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Роли пользователей">
        <table style={styles.table}>
          <thead>
            <tr><th style={styles.th}>Роль</th><th style={styles.th}>Возможности</th></tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}><b>Пациент</b></td>
              <td style={styles.td}>Ведение дневника, прохождение тестов, просмотр дашборда с ИПС и графиками, обмен сообщениями с врачом</td>
            </tr>
            <tr>
              <td style={styles.td}><b>Врач</b></td>
              <td style={styles.td}>Сводная панель, управление списком пациентов, просмотр аналитики и графиков, сравнение периодов, заметки, настройка порогов, экспорт данных, печать отчётов</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 32 }}>
        MS Health Monitoring System | Дипломный проект
      </p>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={styles.section}>
    <h2 style={styles.sectionTitle}>{title}</h2>
    {children}
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  section: {
    backgroundColor: 'var(--bg-card)',
    padding: 24,
    borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    color: '#2563eb',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '2px solid var(--border-color)',
  },
  formula: {
    backgroundColor: 'var(--bg-card-alt)',
    border: '1px solid var(--border-color)',
    padding: '12px 20px',
    borderRadius: 8,
    fontFamily: 'serif',
    fontSize: 16,
    textAlign: 'center',
    margin: '12px 0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 8,
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    backgroundColor: 'var(--bg-table-header)',
    fontSize: 13,
    fontWeight: 600,
    borderBottom: '1px solid var(--border-color)',
  },
  td: {
    padding: '8px 12px',
    fontSize: 14,
    borderBottom: '1px solid var(--border-light)',
  },
};

export default AboutPage;
